import { useLayoutEffect, useMemo, useState, type RefObject } from 'react';
import { CHROME, selectHidden, type PieceId } from './barPlan.ts';

/** Space each measured part occupies besides its own box: the flex gap that follows it. */
const PIECE_GAP = 16;
/** Border and padding around the markets group, added to the part that leaves last. */
const MARKETS_FRAME = 36;
const MAX_SLACK = 640;

type Widths = Partial<Record<PieceId, number>>;

function measure(root: HTMLElement): Widths {
  const widths: Widths = {};
  for (const element of root.querySelectorAll<HTMLElement>('[data-piece]')) {
    const id = element.dataset.piece as PieceId;
    widths[id] = (widths[id] ?? 0) + Math.ceil(element.getBoundingClientRect().width) + PIECE_GAP;
  }
  const lastMarket = (['usd', 'gold', 'eur', 'btc', 'extras'] as const).find((id) => widths[id]);
  if (lastMarket) widths[lastMarket]! += MARKETS_FRAME;
  return widths;
}

const same = (a: Widths, b: Widths) => Object.keys({ ...a, ...b }).every((key) => a[key as PieceId] === b[key as PieceId]);

/**
 * Decides, from the bar's real width and the natural width of each widget (measured on an off-screen copy),
 * which parts to hide. If the bar still overflows after that — a font, zoom or scrollbar the estimate did
 * not foresee — it sheds a little more until nothing is clipped.
 */
export function useBarLayout(bar: RefObject<HTMLElement | null>, measurer: RefObject<HTMLElement | null>, opts: { bell: boolean; date: boolean; seconds: boolean }) {
  const [width, setWidth] = useState(0);
  const [widths, setWidths] = useState<Widths>({});
  const [slack, setSlack] = useState(0);

  useLayoutEffect(() => {
    const element = bar.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => {
      setWidth(Math.round(entry!.contentRect.width));
      setSlack(0);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [bar]);

  useLayoutEffect(() => {
    const element = measurer.current;
    if (!element) return;
    const update = () => {
      const next = measure(element);
      setWidths((current) => (same(current, next) ? current : next));
      setSlack(0);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, [measurer]);

  const hidden = useMemo(() => {
    if (width === 0) return new Set<PieceId>();
    const fixed = CHROME.core + (opts.bell ? CHROME.bell : 0) + (opts.seconds ? CHROME.seconds : 0);
    return selectHidden(width - slack, { ...widths, ...(opts.date ? { date: CHROME.date } : {}) }, fixed);
  }, [width, widths, slack, opts.bell, opts.date, opts.seconds]);

  // Runs after every render; it only acts while something is clipped, and the slack it adds is bounded.
  useLayoutEffect(() => {
    const element = bar.current;
    if (!element || width === 0 || slack >= MAX_SLACK) return;
    const main = element.querySelector<HTMLElement>('.infobar-main');
    if (element.scrollWidth > element.clientWidth || (main && main.scrollWidth > main.clientWidth)) setSlack((value) => value + 16);
  });

  return hidden;
}
